import { shouldShowVipLikedYouPaywall } from "@features/access";
import { VipLikedYouPaywallModal } from "@features/payment/components/VipLikedYouPaywallModal";
import { shouldShowVipLikedYouPaywallModal } from "@features/payment/paywallModalRoutes";
import AskForAngelaSheet from "@features/safety/components/AskForAngelaSheet";
import { isCompleteUser } from "@schemas/users.validators";
import { BottomTabBar } from "@shared/components/BottomTabBar";
import TopAppBar, { type TopAppBarProps } from "@shared/components/TopAppBar";
import useCurrentUser from "@shared/queries/useCurrentUser";
import clsx from "clsx";
import { Shield } from "lucide-react";
import { useState } from "react";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";

/**
 * Context provided by MainLayout to child routes via React Router's outlet context
 * (`<Outlet context={...} />` + `useOutletContext`). Allows pages to configure the layout header.
 *
 * **How to use setHeader:**
 * - Call only from inside a `useEffect` (so the header updates when the page mounts/unmounts).
 * - To show a header: `setHeader({ variant, title?, onBack?, centerContent?, onSave?, menuOptions? })`.
 * - To hide the header: `setHeader(null)`.
 * - **Always clean up:** return `() => setHeader(null)` from the effect so the header is cleared when leaving the route.
 *
 * @example
 * ```tsx
 * const { setHeader } = useLayoutContext();
 *
 * useEffect(() => {
 *   setHeader({ variant: "back", title: "Profile", onBack: () => navigate(-1) });
 *   return () => setHeader(null);
 * }, [setHeader]);
 * ```
 */
export interface LayoutContext {
  /**
   * Configure the top app bar for this page. Pass `null` to hide it.
   * Use only in useEffect; clean up with `() => setHeader(null)`.
   */
  setHeader: (config: TopAppBarProps | null) => void;
  /** Opens the Ask for Angela safety sheet */
  openAskForAngela: () => void;
}

/**
 * Access the layout context from any page rendered within MainLayout.
 * Implemented via react-router-dom's useOutletContext; must be called from a
 * component that is a direct or nested child of MainLayout's <Outlet />.
 *
 * @returns The layout context (setHeader) provided by MainLayout's Outlet
 * @throws If called outside of MainLayout's Outlet context
 */
export function useLayoutContext() {
  return useOutletContext<LayoutContext>();
}

/**
 * Main application layout with optional top header and fixed bottom tab bar.
 *
 * Uses react-router-dom outlet context: passes { setHeader } via
 * <Outlet context={{ setHeader }} /> so child routes can configure the header
 * via useLayoutContext() (which calls useOutletContext<LayoutContext>()).
 * Pages without a header simply don't call setHeader (or pass null).
 *
 * Structure:
 * - TopAppBar (fixed, conditional) - controlled by child pages via context
 * - Main content area (flex-1, scrollable) - renders child routes
 * - BottomTabBar (fixed) - always visible for main navigation
 *
 */
export function MainLayout() {
  const [headerConfig, setHeaderConfig] = useState<TopAppBarProps | null>(null);
  const [angelaOpen, setAngelaOpen] = useState(false);
  const location = useLocation();
  const { user } = useCurrentUser();

  const vipLikedYouPaywallOpen =
    shouldShowVipLikedYouPaywallModal(location.pathname) &&
    Boolean(user && isCompleteUser(user) && shouldShowVipLikedYouPaywall(user));

  const openAskForAngela = () => setAngelaOpen(true);

  return (
    <div className="flex h-dvh flex-col bg-lavender-400">
      {headerConfig && <TopAppBar {...headerConfig} />}

      <main className={clsx("flex min-h-0 flex-1 flex-col pb-bottom-tab-bar", headerConfig && "pt-14")}>
        <Outlet context={{ setHeader: setHeaderConfig, openAskForAngela }} />
      </main>

      <button
        type="button"
        onClick={openAskForAngela}
        className={clsx(
          "fixed bottom-[calc(var(--height-bottom-tab-bar)+env(safe-area-inset-bottom,0px)+0.75rem)] right-3 z-[65]",
          "inline-flex h-10 items-center gap-1.5 rounded-full border border-gray-200 bg-white/95 px-3",
          "text-xs font-medium text-gray-700 shadow-sm",
          "hover:bg-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
        )}
        aria-label="Ask for Angela"
        data-testid="ask-for-angela-fab"
      >
        <Shield className="h-4 w-4 text-pink-600" aria-hidden />
        Help
      </button>

      <BottomTabBar />

      {vipLikedYouPaywallOpen ? <VipLikedYouPaywallModal open /> : null}
      <AskForAngelaSheet open={angelaOpen} onClose={() => setAngelaOpen(false)} />
    </div>
  );
}
